import { SwaggerException } from '../web-api-client';

/**
 * Interface for ValidationProblemDetails from ASP.NET Core
 */
interface ValidationProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: { [key: string]: string[] };
}

/**
 * Interface for ProblemDetails from ASP.NET Core
 */
interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
}

/**
 * Helper function to extract validation errors from ValidationProblemDetails
 */
function extractValidationErrors(responseData: ValidationProblemDetails | ProblemDetails): string | null {
  // Check if it's a ValidationProblemDetails (has errors property)
  if ('errors' in responseData && responseData.errors) {
    const validationErrors = responseData.errors;
    const errorMessages: string[] = [];
    
    // Format validation errors: "Field: Error1, Error2"
    for (const [field, messages] of Object.entries(validationErrors)) {
      if (messages && messages.length > 0) {
        const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
        const combinedMessages = messages.join(', ');
        errorMessages.push(`${fieldName}: ${combinedMessages}`);
      }
    }
    
    if (errorMessages.length > 0) {
      return errorMessages.join('\n');
    }
  }
  return null;
}

/**
 * Extracts a user-friendly error message from an API error.
 * Handles ValidationProblemDetails, ProblemDetails, and SwaggerException.
 * 
 * @param error - The error object from a catch block
 * @param defaultMessage - Default message if error cannot be parsed
 * @returns A formatted error message string
 */
export function getErrorMessage(error: unknown, defaultMessage: string = 'Ocorreu um erro inesperado'): string {
  // First, check if the error itself is already a ValidationProblemDetails or ProblemDetails object
  // (This can happen when the API client throws the parsed result directly)
  if (error && typeof error === 'object') {
    const errorObj = error as any;
    
    // Check if it's a ValidationProblemDetails
    if ('errors' in errorObj && errorObj.errors) {
      const validationError = extractValidationErrors(errorObj as ValidationProblemDetails);
      if (validationError) {
        return validationError;
      }
    }
    
    // Check if it's a ProblemDetails with detail
    if ('detail' in errorObj && errorObj.detail) {
      return errorObj.detail;
    }
    
    // Check if it's a ProblemDetails with title
    if ('title' in errorObj && errorObj.title && errorObj.title !== 'One or more validation errors occurred.') {
      return errorObj.title;
    }
  }
  
  // Handle SwaggerException
  if (SwaggerException.isSwaggerException(error)) {
    const swaggerError = error as SwaggerException;
    
    // First check if the result property contains the parsed object
    if (swaggerError.result) {
      const result = swaggerError.result as any;
      
      // Check if result is a ValidationProblemDetails
      if ('errors' in result && result.errors) {
        const validationError = extractValidationErrors(result as ValidationProblemDetails);
        if (validationError) {
          return validationError;
        }
      }
      
      // Check if result is a ProblemDetails
      if ('detail' in result && result.detail) {
        return result.detail;
      }
      
      if ('title' in result && result.title) {
        return result.title;
      }
    }
    
    // Try to parse the response as JSON
    if (swaggerError.response) {
      try {
        const responseData = JSON.parse(swaggerError.response) as ValidationProblemDetails | ProblemDetails;
        
        // Check for validation errors
        const validationError = extractValidationErrors(responseData);
        if (validationError) {
          return validationError;
        }
        
        // Check if it's a ProblemDetails (has detail or title)
        if ('detail' in responseData && responseData.detail) {
          return responseData.detail;
        }
        
        if ('title' in responseData && responseData.title) {
          return responseData.title;
        }
      } catch (parseError) {
        // If parsing fails, fall through to use the response text or message
      }
      
      // If response is not JSON or parsing failed, try to use the response text if it's meaningful
      if (swaggerError.response.trim().length > 0 && swaggerError.response.trim().length < 500) {
        return swaggerError.response;
      }
    }
    
    // Fall back to the message
    if (swaggerError.message && swaggerError.message.trim().length > 0) {
      return swaggerError.message;
    }
  }
  
  // Handle regular Error objects
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // Fall back to default message
  return defaultMessage;
}

/**
 * Checks if an error is related to wallet session issues
 * @param error - The error object to check
 * @returns true if the error is wallet-related, false otherwise
 */
export function isWalletSessionError(error: unknown): boolean {
  const errorMessage = getErrorMessage(error, '').toLowerCase();
  
  // Check for common wallet session error patterns
  const walletErrorPatterns = [
    'wallet session',
    'unlock your wallet',
    'wallet is locked',
    'no active wallet session',
    'wallet not unlocked',
    'please unlock wallet',
    'wallet session expired',
    'wallet session not found',
  ];
  
  return walletErrorPatterns.some(pattern => errorMessage.includes(pattern));
}

/**
 * Extracts wallet error information from an error object
 * @param error - The error object to analyze
 * @returns Object with wallet error details or null if not a wallet error
 */
export function getWalletErrorInfo(error: unknown): { 
  isWalletError: boolean; 
  message: string;
  requiresUnlock: boolean;
} | null {
  if (!isWalletSessionError(error)) {
    return null;
  }
  
  const message = getErrorMessage(error, 'Wallet session error');
  
  return {
    isWalletError: true,
    message,
    requiresUnlock: true,
  };
}

/**
 * Extracts error messages and formats them for display in a toast.
 * Returns an object with title and description suitable for toast notifications.
 * 
 * @param error - The error object from a catch block
 * @param defaultTitle - Default title if error cannot be parsed
 * @param defaultDescription - Default description if error cannot be parsed
 * @returns An object with title and description for toast
 */
export function getErrorToast(
  error: unknown,
  defaultTitle: string = 'Erro',
  defaultDescription: string = 'Ocorreu um erro inesperado'
): { title: string; description: string } {
  const errorMessage = getErrorMessage(error, defaultDescription);
  
  // If it's a validation error (contains newlines or multiple lines), format it nicely
  if (errorMessage.includes('\n')) {
    const lines = errorMessage.split('\n');
    if (lines.length > 1) {
      return {
        title: 'Erros de Validação',
        description: lines.join('\n')
      };
    }
  }
  
  // Check if it's a SwaggerException to determine appropriate title
  if (SwaggerException.isSwaggerException(error)) {
    const swaggerError = error as SwaggerException;
    
    if (swaggerError.status === 400) {
      return {
        title: 'Erro de Validação',
        description: errorMessage
      };
    } else if (swaggerError.status === 401) {
      return {
        title: 'Não Autorizado',
        description: 'As suas credenciais não são válidas ou expiraram.'
      };
    } else if (swaggerError.status === 403) {
      return {
        title: 'Acesso Negado',
        description: 'Não tem permissão para realizar esta operação.'
      };
    } else if (swaggerError.status === 404) {
      return {
        title: 'Não Encontrado',
        description: errorMessage || 'O recurso solicitado não foi encontrado.'
      };
    } else if (swaggerError.status >= 500) {
      return {
        title: 'Erro do Servidor',
        description: 'Ocorreu um erro no servidor. Por favor, tente novamente mais tarde.'
      };
    }
  }
  
  return {
    title: defaultTitle,
    description: errorMessage
  };
}

